import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Modal, Tooltip } from 'antd';
import { capitalize } from 'lodash';
import {
  CaretRightOutlined,
  ScheduleOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import classNames from 'classnames';

import SchedulePipelineModal from '../SchedulePipelineModal';
import PipelineHistoryDrawer from './PipelineHistoryDrawer';
import {
  PipelinesContainer,
  TableActionsWrapper,
  TableLastRunStatusWrapper,
  TableNextScheduleWrapper,
  TableStatusWrapper,
  TableStyled
} from './styled';

import { PIPELINE_TYPES, en } from './constants';
import {
  convertISOToDateTimeWithoutSeconds,
  getCurrentTimezoneText
} from '../../../util/timezone';

const Pipelines = ({
  className,
  moduleId,
  apiActions,
  pipelines,
  pipelinesLoading,
  pipelinesScheduleData,
  pipelinesUnScheduleData,
  pipelinesScheduleInfo,
  pipelinesScheduleInfoLoading,
  pipelinesHistory,
  pipelinesHistoryIsLoading,
  pipelineStop
}) => {
  const [stopPipelineModal, setStopPipelineModal] = useState({
    isOpen: false,
    data: null
  });

  const [scheduleModal, setScheduleModal] = useState({
    isOpen: false,
    data: null
  });

  const [historyDrawer, setHistoryDrawer] = useState({
    isOpen: false,
    data: null
  });

  const [modal, contextHolder] = Modal.useModal();

  const getPipelinesPayload = useMemo(
    () => ({
      moduleId: moduleId
    }),
    [moduleId]
  );

  const handleGetPipelines = () => {
    apiActions.getAMLSCommonPipelines(getPipelinesPayload);
  };

  useEffect(() => {
    handleGetPipelines();
  }, [getPipelinesPayload]);

  const handleRunPipeline = useCallback((id) => {
    let payload = { id, moduleId };
    apiActions.postAMLSCommonPipelinesRun(payload);
  }, []);

  // custom func to sort the pipelines list to show Active items first
  const customSort = (a, b) => {
    if (a.pipelineStatus === 'ACTIVE' && b.pipelineStatus !== 'ACTIVE') {
      return -1; // a comes before b
    }
    if (a.pipelineStatus !== 'ACTIVE' && b.pipelineStatus === 'ACTIVE') {
      return 1; // b comes before a
    }
    return 0; // No change in order
  };

  const pipelinesSorted = pipelines.sort(customSort);

  const handleRunAutomationClick = useCallback(
    (data) => {
      modal.confirm({
        title: en.actions.runPipeline.confirmTitle,
        icon: <ExclamationCircleOutlined />,
        content: (
          <span
            dangerouslySetInnerHTML={{
              __html: en.actions.runPipeline.confirmContent.replace(
                '{{name}}',
                data.name
              )
            }}
          />
        ),
        okText: en.actions.runPipeline.confirmOkText,
        cancelText: 'Cancel',
        onOk: () => handleRunPipeline(data.id)
      });
    },
    [handleRunPipeline]
  );

  const handleSchedulePipelineClick = useCallback((data) => {
    setScheduleModal({
      isOpen: true,
      data
    });
  }, []);

  const handleHistoryClick = useCallback((data) => {
    setHistoryDrawer({
      isOpen: true,
      data
    });
  }, []);

  const handleStopPipelineClick = useCallback((data) => {
    setStopPipelineModal({
      isOpen: true,
      data
    });
  }, []);

  useEffect(() => {
    if (scheduleModal?.isOpen) {
      setScheduleModal({
        isOpen: false,
        data: scheduleModal?.data
      });
      handleGetPipelines();
    }
  }, [pipelinesScheduleData?.id]);

  useEffect(() => {
    if (scheduleModal?.isOpen) {
      setScheduleModal({
        isOpen: false,
        data: null
      });
      handleGetPipelines();
    }
  }, [pipelinesUnScheduleData?.id]);

  const handleScheduleModalClose = useCallback(() => {
    setScheduleModal({
      isOpen: false,
      data: null
    });
  }, []);

  const handleHistoryDrawerClose = useCallback(() => {
    setHistoryDrawer({
      isOpen: false,
      data: null
    });
  }, []);

  const handleStopPipelineModalClose = useCallback((data) => {
    setStopPipelineModal({
      isOpen: false,
      data
    });
  }, []);

  useEffect(() => {
    if (stopPipelineModal?.isOpen && pipelineStop?.requestCompleted) {
      setStopPipelineModal({
        isOpen: false,
        data: stopPipelineModal.data
      });
    }
  }, [pipelineStop?.requestCompleted]);

  const columns = useMemo(
    () => [
      {
        title: en.columns.pipelineName,
        dataIndex: 'name',
        render: (value, data) => (
          <div>
            {value}{' '}
            {data.pipelineType === PIPELINE_TYPES.STREAMING ? (
              <Badge
                count={en.streamingBadgeText}
                style={{
                  borderRadius: '33px',
                  background: 'var(--red-red-6, #DF6057)'
                }}
              />
            ) : null}
          </div>
        )
      },
      {
        title: en.columns.description,
        dataIndex: 'description'
      },
      {
        title: en.columns.schedule,
        dataIndex: 'schedule',
        render: (value) => <>{value || '-'}</>
      },
      {
        title: en.columns.nextSchedule.replace(
          '{{tz}}',
          getCurrentTimezoneText()
        ),
        dataIndex: 'nextSchedule',
        render: (value) => (
          <TableNextScheduleWrapper>
            {value ? convertISOToDateTimeWithoutSeconds(value) : '-'}
          </TableNextScheduleWrapper>
        )
      },
      {
        title: en.columns.runStatus.replace('{{tz}}', getCurrentTimezoneText()),
        dataIndex: 'lastRunStatus',
        render: (value, data) => (
          <TableLastRunStatusWrapper>
            <p
              className={`status-text ${
                data.lastRunStatus?.replace(' ', '_').toLowerCase() || '-'
              }`}>
              {capitalize(data.lastRunStatus)}
            </p>
            {data.lastRunTime ? (
              <p className="date-time-text">
                {convertISOToDateTimeWithoutSeconds(data.lastRunTime)}
              </p>
            ) : null}
          </TableLastRunStatusWrapper>
        )
      },
      {
        title: en.columns.status,
        dataIndex: 'pipelineStatus',
        render: (value) => (
          <TableStatusWrapper>
            <p className={`piepline-status-text ${value?.toLowerCase()}`}>
              {value}
            </p>
          </TableStatusWrapper>
        )
      },
      {
        title: en.columns.actions,
        dataIndex: 'action',
        render: (value, data) => (
          <TableActionsWrapper>
            <Tooltip title={en.actions.runPipeline.tooltip}>
              <CaretRightOutlined
                role="button"
                disabled={
                  !data.isTriggerEnabled ||
                  (data?.lastRunStatus &&
                    data.lastRunStatus !== 'COMPLETED' &&
                    data.lastRunStatus !== 'FAILED')
                }
                onClick={() => {
                  if (data.isTriggerEnabled) {
                    data.isTriggerEnabled && handleRunAutomationClick(data);
                  }
                }}
                className={classNames({
                  disabled:
                    !data.isTriggerEnabled ||
                    (data?.lastRunStatus &&
                      data.lastRunStatus !== 'COMPLETED' &&
                      data.lastRunStatus !== 'FAILED')
                })}
              />
            </Tooltip>
            <Tooltip title={en.actions.schedule.tooltip}>
              <ScheduleOutlined
                role="button"
                onClick={() => {
                  if (data.isScheduleEnabled) {
                    data.isScheduleEnabled && handleSchedulePipelineClick(data);
                    apiActions.getAMLSCommonPipelinesScheduleInfo({
                      id: data?.id,
                      moduleId
                    });
                  }
                }}
                className={classNames({ disabled: !data.isScheduleEnabled })}
              />
            </Tooltip>
            <Tooltip title={en.actions.history.tooltip}>
              <HistoryOutlined
                role="button"
                onClick={() => {
                  if (data.isHistoryEnabled) {
                    data.isHistoryEnabled && handleHistoryClick(data);
                  }
                }}
                className={classNames({ disabled: !data.isHistoryEnabled })}
              />
            </Tooltip>
            <Tooltip title={en.actions.stopPipeline.tooltip}>
              <CloseCircleOutlined
                role="button"
                disabled={
                  data?.pipelineType !== 'STREAMING' ||
                  (data?.pipelineType == 'STREAMING' &&
                    data?.lastRunStatus !== 'RUNNING')
                }
                onClick={() => {
                  if (
                    data?.pipelineType == 'STREAMING' &&
                    data?.lastRunStatus == 'RUNNING' &&
                    data?.id
                  ) {
                    handleStopPipelineClick(data);
                  }
                }}
                className={classNames({
                  disabled:
                    data?.pipelineType !== 'STREAMING' ||
                    (data?.pipelineType == 'STREAMING' &&
                      data?.lastRunStatus !== 'RUNNING')
                })}
              />
            </Tooltip>
          </TableActionsWrapper>
        )
      }
    ],
    []
  );

  return (
    <div>
      <PipelinesContainer className={className}>
        <TableStyled
          id="pipelines-table"
          loading={pipelinesLoading}
          columns={columns}
          rowKey={'id'}
          dataSource={pipelinesSorted}
          pagination={false}
        />
      </PipelinesContainer>
      <SchedulePipelineModal
        open={scheduleModal.isOpen}
        selectedPipeline={scheduleModal.data}
        onClose={handleScheduleModalClose}
        apiActions={apiActions}
        pipelinesScheduleInfo={pipelinesScheduleInfo}
        pipelinesScheduleInfoLoading={pipelinesScheduleInfoLoading}
        moduleId={moduleId}
      />
      <PipelineHistoryDrawer
        open={historyDrawer.isOpen}
        onClose={handleHistoryDrawerClose}
        selectedPipeline={historyDrawer.data}
        apiActions={apiActions}
        pipelinesHistory={pipelinesHistory}
        pipelinesHistoryIsLoading={pipelinesHistoryIsLoading}
      />
      {/* Pipeline stop modal starts*/}
      <Modal
        title="Stop Pipeline"
        visible={stopPipelineModal.isOpen}
        onOk={() =>
          apiActions?.postAMLSCommonPipelineStop({
            id: stopPipelineModal.data.id,
            moduleId
          })
        }
        onCancel={handleStopPipelineModalClose}
        okText="Stop"
        confirmLoading={pipelineStop?.isLoading}>
        <p>Do you really want to Stop the pipeline?</p>
        <p>
          Name: <b>{stopPipelineModal?.data?.name}</b>
        </p>
      </Modal>
      {/* Pipeline stop modal ends*/}
      {contextHolder}
    </div>
  );
};

Pipelines.propTypes = {
  className: PropTypes.string,
  moduleId: PropTypes.string,
  apiActions: PropTypes.object,
  pipelines: PropTypes.array,
  pipelinesLoading: PropTypes.bool,
  pipelinesScheduleData: PropTypes.object,
  pipelinesUnScheduleData: PropTypes.object,
  pipelinesScheduleInfo: PropTypes.object,
  pipelinesScheduleInfoLoading: PropTypes.bool,
  pipelinesHistory: PropTypes.object,
  pipelinesHistoryIsLoading: PropTypes.bool,
  pipelineStop: PropTypes.object
};

export default Pipelines;
